import { useContext, useMemo, useRef, useState } from 'react';
import type { DragEvent } from 'react';
import { useSession } from '../session/context';
import { useLicense } from '../license/context';
import { UpgradePrompt } from '../license/UpgradePrompt';
import { BatchContext } from '../batch/context';
import { BatchPanel } from '../batch/BatchPanel';
import { ingestFiles, applyInputs, clearBatch } from '../batch/ingest';
import { workerRunner as batchWorkerRunner } from '../batch/workerRunner';
import type { BatchRunner } from '../batch/types';
import { friendlyErrorMessage } from '../session/session';
import {
  exportAndDownload,
  isProTarget,
  targetName,
  type TargetId,
} from '../session/exportDownload';
import { useWindowDropDelegate } from '../session/useWindowFileDrop';
import { type ParseWarning } from '../../core';
import { FormatPicker } from '../convert/FormatPicker';
import { LossMatrix, WarningList } from '../convert/LossMatrix';
import { isFirstRun, markOnboarded, sampleFile } from '../onboarding';
import { ConfirmDialog } from '../components/ConfirmDialog';
import { parseConvertParams } from '../convertParams';
import { track } from '../analytics/client';
import './Converter.css';

/** Read the `?from=&to=` deep link once; jsdom/browser both have `window`. */
function deepLinkParams(): { from?: TargetId; to?: TargetId } {
  return parseConvertParams(typeof window === 'undefined' ? '' : window.location.search);
}

/**
 * Converter mode (spec §4.1 #1, §8): open subtitle files (button or drag-drop)
 * via one auto-scaling drop zone (cue-forge-9px) — one file converts inline with
 * a live loss matrix; several files switch to the batch workspace (Pro, gated
 * inline). See the auto-detected source format and any repair/parse warnings,
 * pick a target, and download. Repair-on-import happens inside the session; here
 * we only surface its results. The `batchRunner` is injectable for tests.
 */
export function Converter({ batchRunner = batchWorkerRunner }: { batchRunner?: BatchRunner } = {}) {
  const {
    loaded,
    error,
    proImportLocked,
    load,
    dirty,
    markClean,
    autosaveEnabled,
    setAutosaveEnabled,
  } = useSession();
  const { isPro } = useLicense();
  // Optional: the Converter also renders in tests without a BatchProvider, so the
  // many-files branch is only available when the provider is present (as in the
  // app shell). Mirrors the Editor's optional QC context.
  const batch = useContext(BatchContext);
  const inputRef = useRef<HTMLInputElement>(null);
  // Format-pair deep link (e.g. /app/?from=srt&to=vtt): seed the target once and
  // keep `from` as a hint (source is detected from a loaded file, never forced).
  const deepLink = useMemo(deepLinkParams, []);
  const [target, setTarget] = useState<TargetId>(() => deepLink.to ?? 'vtt');
  const [dragOver, setDragOver] = useState(false);
  const [dropWarnings, setDropWarnings] = useState<ParseWarning[]>([]);
  const [exportError, setExportError] = useState<string | null>(null);
  // Visible + announced confirmation of the last successful convert (w28.13) —
  // set only on the user's convert action, never by background work.
  const [convertStatus, setConvertStatus] = useState('');
  const [firstRun, setFirstRun] = useState(isFirstRun);
  // Ingest notices when a multi-file drop collapses to zero/one accepted file, so
  // an all-rejected drop is never a silent no-op (cue-forge-zxw.8); the batch
  // workspace surfaces its own notices when it actually mounts.
  const [dropNotices, setDropNotices] = useState<string[]>([]);
  // A single-file load parked behind the batch-discard confirm (cue-forge-drt.19
  // — the styled ConfirmDialog replaces window.confirm).
  const [pendingSingle, setPendingSingle] = useState<File | null>(null);

  const targetIsPro = isProTarget(target);
  const locked = targetIsPro && !isPro;
  // The batch workspace takes over when more than one file is queued.
  const batchActive = !!batch && batch.files.length > 0;
  // A 0-cue document would "convert" to an empty deliverable — block it with an
  // explanation instead of succeeding silently (cue-forge-w28.16, drt.15).
  const noCues = !!loaded && loaded.doc.cues.length === 0;

  function proceedLoad(file: File) {
    markOnboarded();
    setFirstRun(false);
    setDropNotices([]);
    setConvertStatus('');
    void load(file, isPro);
  }

  // Discarding a queued batch to start a single convert asks first (cue-forge-zxw.8
  // — no silent data loss); an empty queue loads without prompting.
  function loadFile(file: File) {
    if (batch && batch.files.length > 0) {
      setPendingSingle(file);
      return;
    }
    proceedLoad(file);
  }

  // One drop zone, auto-scaling by count (cue-forge-9px): a single file converts
  // inline; several switch to the batch workspace. The batch-vs-single decision
  // keys on the ACCEPTED count, not the picked count (cue-forge-zxw.8): the
  // intake caps can collapse a multi-drop to one (→ single card) or zero
  // (→ notices only), so a lone survivor never shows the batch UI for one file.
  async function onFiles(files: File[]) {
    if (files.length === 0) return;
    if (files.length === 1 || !batch) {
      loadFile(files[0]!);
      return;
    }
    markOnboarded();
    setFirstRun(false);
    setConvertStatus('');
    const { inputs, notices } = await ingestFiles(files);
    if (inputs.length > 1) {
      setDropNotices([]);
      applyInputs(batch, inputs, notices);
      return;
    }
    // Collapsed to zero/one: no batch. Report the notices here (the batch panel
    // won't mount to show them) and convert the lone survivor inline if any.
    clearBatch(batch);
    setDropNotices(notices);
    if (inputs.length === 1) {
      void load(new File([inputs[0]!.bytes as BlobPart], inputs[0]!.name), isPro);
    }
  }

  // Window-level drops while Convert is the active mode take this exact intake
  // (cue-forge-w28.14): same batch-discard confirm, same multi-file → batch
  // semantics as the dedicated dropzone — never an invisible session.load.
  useWindowDropDelegate((files) => void onFiles(files));

  function onDrop(e: DragEvent<HTMLDivElement>) {
    e.preventDefault();
    setDragOver(false);
    void onFiles(Array.from(e.dataTransfer.files));
  }

  async function onConvert() {
    if (!loaded || locked || noCues) return;
    setExportError(null);
    setConvertStatus('');
    try {
      const { warnings, filename } = await exportAndDownload(loaded, target);
      setDropWarnings(warnings);
      markClean();
      // Success confirmation (w28.13): visible + announced, mirroring StudioExport.
      // Names the file the export actually downloaded — never re-derived.
      setConvertStatus(`Converted to ${targetName(target)} — downloaded ${filename}`);
      track('export', { format: target, surface: 'convert' });
    } catch (err) {
      setDropWarnings([]);
      // Expected errors (e.g. charset preflight) carry user-facing copy; anything
      // else collapses to a calm sentence instead of a raw stack string (w28.12).
      setExportError(
        friendlyErrorMessage(
          err,
          `Couldn't convert this file to ${targetName(target)}. Try a different target format, or re-open the source file and try again.`,
        ),
      );
    }
  }

  return (
    <section className={`cf-converter${loaded || batchActive ? '' : ' cf-converter--empty'}`}>
      <h1 className="cf-converter__title">Convert</h1>

      {!loaded && !batchActive && (
        <p className="cf-empty__lead">
          Drop a subtitle file to convert between formats with a live loss matrix that shows exactly
          what each target keeps — or drop several to batch-convert them into one ZIP. Everything
          runs here; nothing uploads.
        </p>
      )}

      {!loaded && !batchActive && deepLink.from && (
        <p className="cf-converter__welcome">
          Converting {deepLink.from.toUpperCase()} to {target.toUpperCase()}? Drop your{' '}
          {`.${deepLink.from}`} file to start.
        </p>
      )}

      <div
        className={`cf-dropzone${dragOver ? ' cf-dropzone--over' : ''}`}
        onDragOver={(e) => {
          e.preventDefault();
          setDragOver(true);
        }}
        onDragLeave={() => setDragOver(false)}
        onDrop={onDrop}
      >
        <p className="cf-dropzone__hint">Drop a subtitle file here — or several to batch — or</p>
        <div className="cf-empty__actions">
          <button type="button" className="cf-btn" onClick={() => inputRef.current?.click()}>
            Open file
          </button>
          <button type="button" className="cf-btn" onClick={() => loadFile(sampleFile())}>
            Try a sample
          </button>
        </div>
        <input
          ref={inputRef}
          type="file"
          multiple
          className="cf-visually-hidden"
          aria-label="Open subtitle file"
          accept=".srt,.vtt,.sbv,.ass,.ssa,.scc,.stl,.ttml,.dfxp,.xml,.itt"
          onChange={(e) => {
            void onFiles(Array.from(e.target.files ?? []));
            e.target.value = '';
          }}
        />
      </div>
      {firstRun && !loaded && !batchActive && (
        <p className="cf-converter__welcome">
          New to CueForge? Everything runs in your browser. Your files never leave this device.
        </p>
      )}

      {error && (
        <p className="cf-converter__error" role="alert">
          {error}
        </p>
      )}
      {exportError && (
        <p className="cf-converter__error" role="alert">
          {exportError}
        </p>
      )}

      {proImportLocked && <UpgradePrompt feature={`${proImportLocked} import`} surface="import" />}

      {/* Ingest notices when a multi-drop collapsed to <=1 accepted file, so an
          all-rejected drop reports why instead of silently doing nothing (zxw.8). */}
      {dropNotices.length > 0 && (
        <div className="cf-warnings" role="status">
          <h2 className="cf-warnings__title">Some files couldn't be added</h2>
          <ul>
            {dropNotices.map((n, i) => (
              <li key={i}>{n}</li>
            ))}
          </ul>
        </div>
      )}

      {/* Always-mounted polite live region: announces load results to AT, and —
          visibly — the last successful convert (w28.13). It must exist before
          the message is set or it is never announced. */}
      <p
        className={convertStatus ? 'cf-converter__status' : 'cf-visually-hidden'}
        role="status"
        aria-live="polite"
      >
        {convertStatus ||
          (loaded ? `Loaded ${loaded.filename} — ${loaded.doc.cues.length} cues` : '')}
      </p>

      {/* Many-files branch (cue-forge-9px): the batch workspace, Pro-gated inline.
          Takes precedence over the single-file card while a queue is present. */}
      {batchActive && <BatchPanel isPro={isPro} runner={batchRunner} />}

      {!batchActive && loaded && (
        <>
          <dl className="cf-source">
            <div>
              <dt>File</dt>
              <dd className="cf-mono">
                {loaded.filename}
                {dirty && <span className="cf-converter__dirty"> (edited)</span>}
              </dd>
            </div>
            <div>
              <dt>Source</dt>
              <dd>
                {targetName(loaded.sourceFormat)} · {loaded.encoding} · {loaded.doc.cues.length}{' '}
                cues
              </dd>
            </div>
          </dl>

          <p className="cf-converter__autosave">
            {autosaveEnabled ? 'Autosaving to this device, never uploaded.' : 'Autosave is off.'}{' '}
            <button
              type="button"
              className="cf-linkbtn"
              onClick={() => setAutosaveEnabled(!autosaveEnabled)}
            >
              {autosaveEnabled ? 'Turn off' : 'Turn on'}
            </button>
          </p>

          <div className="cf-convert-row">
            <FormatPicker
              label="Target format"
              ariaLabel="Target format"
              value={target}
              onChange={setTarget}
              isPro={isPro}
            />
            <button
              type="button"
              className="cf-btn cf-btn--primary"
              onClick={() => void onConvert()}
              disabled={locked || noCues}
            >
              Convert
              {locked && (
                <span className="cf-lock" aria-hidden="true">
                  {' \u{1F512}'}
                </span>
              )}
            </button>
          </div>

          {/* Disabled-with-reason (drt.15): say WHY Convert is off for a 0-cue doc. */}
          {noCues && (
            <p className="cf-converter__note">
              Nothing to convert — this file contains no cues. It may be empty or not a valid
              subtitle file.
            </p>
          )}

          <LossMatrix doc={loaded.doc} selected={target} />

          {locked && <UpgradePrompt feature={`${targetName(target)} export`} surface="convert" />}

          <WarningList
            title="Import"
            warnings={[
              ...loaded.repairs.map((r) => r.message),
              ...loaded.parseWarnings.map((w) => w.message),
            ]}
          />
          <WarningList title="Conversion" warnings={dropWarnings.map((w) => w.message)} />
        </>
      )}

      {/* Styled confirm before a queued batch is discarded for a single convert
          (cue-forge-zxw.8 no-silent-data-loss, drt.19 no window.confirm). */}
      {pendingSingle && batch && (
        <ConfirmDialog
          title="Discard queued files?"
          message={`Discard the ${batch.files.length} queued files and convert this one instead?`}
          confirmLabel="Discard and convert"
          onConfirm={() => {
            const file = pendingSingle;
            setPendingSingle(null);
            clearBatch(batch);
            proceedLoad(file);
          }}
          onCancel={() => setPendingSingle(null)}
        />
      )}
    </section>
  );
}
